import { useState } from 'react';
import { post } from 'core/utils/request';
import urlResolver from 'admin/utils/URLResolver';
import { useUserCourseProgress } from 'edrolo/domains/bySchoolClass';

interface IUseSelfRating {
  data: number | null;
  loading: boolean;
  error: null | Error | unknown;
  updateSelfRating: (rating: number, lessonId: number) => Promise<void>;
}

export const useSelfRating = (
  userId: number,
  classId: number,
  lessonId: number
): IUseSelfRating => {
  const [error, setError] = useState<null | Error | unknown>(null);

  const { data: userProgressData, mutate } = useUserCourseProgress(
    userId,
    classId
  );

  const updateSelfRating = async (rating: number, lessonId: number) => {
    const optimisticData = {
      ...userProgressData,
      lessons: {
        ...userProgressData?.lessons,
        [lessonId]: {
          ...userProgressData?.lessons[lessonId],
          self_rating: rating,
        },
      },
    };

    const options = {
      revalidate: false, // we're happy with our optimistic update
      populateCache: false, // the response from self rating endpoint is not used
      rollbackOnError: true, // if there's an error, rollback to the previous state
      optimisticData,
    };

    try {
      mutate(
        post(urlResolver.getPath('ajax:self-assessment'), {
          branch_id: lessonId,
          rating,
        }),
        options
      );
    } catch (err: unknown) {
      setError(err);
      console.error(err);
    }
  };

  return {
    data: userProgressData?.lessons[lessonId]?.self_rating || null,
    loading: !userProgressData?.lessons[lessonId]?.self_rating && !error,
    error,
    updateSelfRating,
  };
};
